import type { ModuleKey, TicketPriority } from '@esa/shared';
import { templatePath, trackError } from './telemetry.js';
/**
 * API client. One place that knows about the token, the base URL and error
 * shapes — so no component builds a fetch by hand.
 */

const TOKEN_KEY = 'esa.ticketing.token';
/**
 * Set only by a deliberate sign-out.
 *
 * "No token" cannot carry this: it is equally a first visit and an expired
 * session, and both of those should sign in without being asked. Only
 * somebody who chose to leave should be met with a stop.
 */
const SIGNED_OUT_KEY = 'esa.ticketing.signedout';
const TENANT_KEY = 'esa.ticketing.tenant';
/** The desks this browser's person was staff in when last seen (lib/deskChoice.ts). */
const STAFF_DESKS_KEY = 'esa.ticketing.staffdesks';
const THEME_KEY = 'esa.ticketing.theme';

/** localStorage can throw in a private window or with site data blocked. */
function safeGet(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}
function safeSet(key: string, value: string | null): void {
  try {
    if (value === null) localStorage.removeItem(key);
    else localStorage.setItem(key, value);
  } catch {
    /* per-viewer convenience only; never load-bearing */
  }
}

export const session = {
  get token(): string | null {
    return safeGet(TOKEN_KEY);
  },
  set token(v: string | null) {
    safeSet(TOKEN_KEY, v);
  },
  get tenant(): string | null {
    return safeGet(TENANT_KEY);
  },
  set tenant(v: string | null) {
    safeSet(TENANT_KEY, v);
  },
  get staffDesks(): string[] | null {
    const raw = safeGet(STAFF_DESKS_KEY);
    if (raw === null) return null;
    try {
      const v = JSON.parse(raw) as unknown;
      return Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string') : null;
    } catch {
      return null;
    }
  },
  set staffDesks(v: string[] | null) {
    safeSet(STAFF_DESKS_KEY, v === null ? null : JSON.stringify(v));
  },
  get theme(): string | null {
    return safeGet(THEME_KEY);
  },
  set theme(v: string | null) {
    safeSet(THEME_KEY, v);
  },
  /**
   * Why there is no session, when that is known.
   *
   * 'user' — they chose to leave. 'expired' — it ended under them, which is
   * not the same thing and should not be worded as though they did it.
   */
  get signedOut(): 'user' | 'expired' | null {
    const v = safeGet(SIGNED_OUT_KEY);
    return v === 'user' || v === 'expired' ? v : null;
  },
  set signedOut(v: 'user' | 'expired' | null) {
    safeSet(SIGNED_OUT_KEY, v);
  },
};

export interface FieldIssue {
  key: string;
  message: string;
}

/**
 * The best sentence available for a failed request.
 *
 * A validation failure names the fields; a domain error carries a message;
 * everything else has only a status. Field messages are written to be read by
 * a person — "say what fixed it", not "fix: required" — which is exactly what
 * a toast wants, and they were being discarded in favour of the error CODE.
 */
export function describeFailure(
  body: { message?: string; error?: string; fields?: FieldIssue[] } | null,
  status: number,
): string {
  const b = body ?? {};
  if (b.message) return b.message;

  const fields = b.fields?.map((f) => f.message).filter(Boolean) ?? [];
  // Joined rather than truncated to the first: "fix" and "selfSolvable" are
  // both missing on a resolve, and being told about one at a time means
  // failing twice.
  if (fields.length) return fields.join(' · ');

  return b.error ?? `request failed with ${status}`;
}

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
    readonly fields?: FieldIssue[],
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

/**
 * Who the stored token belongs to.
 *
 * The payload of a JWT is not secret and is readable without the key, which is
 * all that is needed here: when a token expires, re-authenticating should
 * bring back the SAME person rather than silently resetting to whichever
 * identity the bootstrap prefers. Somebody demonstrating as an agent must not
 * be promoted to platform admin by an overnight expiry.
 */
function oidFromToken(token: string | null): string | null {
  if (!token) return null;
  try {
    const payload = token.split('.')[1];
    if (!payload) return null;
    const json = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
    return (JSON.parse(json) as { oid?: string }).oid ?? null;
  } catch {
    return null;
  }
}

/**
 * Pick the dev identity to sign in as. Shared with the bootstrap so the two
 * cannot disagree about who the default is.
 */
export function pickDevIdentity(users: DevUser[], preferOid?: string | null): DevUser | undefined {
  if (preferOid) {
    const same = users.find((u) => u.oid === preferOid);
    if (same) return same;
  }
  return (
    users.find((u) => u.roles.some((r) => r?.endsWith(':platform_admin'))) ??
    users.find((u) => u.roles.some((r) => r?.endsWith(':tenant_admin'))) ??
    users[0]
  );
}

/**
 * How the current provider gets a fresh token.
 *
 * REGISTERED RATHER THAN IMPORTED, because this module cannot import the
 * one that knows: `sso.ts` already depends on this file for `session`, and
 * reaching back the other way would be a cycle. It also keeps the rule that
 * this file knows about tokens and nothing about who issues them.
 *
 * Null means the dev provider, which renews from the seeded identities
 * below.
 */
/**
 * Which provider is in force, told to us at boot.
 *
 * Only so the dev renewal path can be skipped when it cannot possibly work.
 * Defaults to 'entra', the conservative guess: wrongly skipping a dev
 * shortcut costs a reload, wrongly calling a route that does not exist
 * produces a 404 in the console that looks like a missing endpoint.
 */
let authProvider: 'dev' | 'entra' = 'entra';

export function setAuthProvider(p: 'dev' | 'entra'): void {
  authProvider = p;
}

let renewer: (() => Promise<string | null>) | null = null;

export function setTokenRenewer(fn: (() => Promise<string | null>) | null): void {
  renewer = fn;
}

/**
 * What to do when the session is gone for good.
 *
 * Registered by the boot component, which is the only thing able to replace
 * the whole page. Without it the best a failed renewal could manage was an
 * error inside whatever screen happened to be open — a red panel saying
 * "one moment" that stayed there, because nothing was coming.
 */
let onSessionLost: (() => void) | null = null;

export function setSessionLostHandler(fn: (() => void) | null): void {
  onSessionLost = fn;
}

/**
 * When the stored token stops being accepted, in epoch milliseconds.
 *
 * Read from the JWT rather than tracked separately: a locally kept expiry
 * and the token's own claim drift the moment anything renews out of band,
 * and the claim is the one the server checks.
 */
function expiresAt(token: string | null): number | null {
  if (!token) return null;
  try {
    const payload = token.split('.')[1];
    if (!payload) return null;
    const json = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
    const exp = (JSON.parse(json) as { exp?: number }).exp;
    return typeof exp === 'number' ? exp * 1000 : null;
  } catch {
    return null;
  }
}

/**
 * Long enough to cover a slow renewal and a slow request after it.
 *
 * Too small and a token expires mid-flight anyway; too large and every
 * session renews constantly for no reason. Two minutes against an hour-long
 * token renews roughly once an hour.
 */
const RENEW_BEFORE_MS = 120_000;

/** Is this token past its own expiry — not close to it, but over? */
export function hasExpired(token: string | null, now = Date.now()): boolean {
  const exp = expiresAt(token);
  return exp !== null && exp <= now;
}

/** Is this token close enough to expiry that the next call would fail? */
export function needsRenewal(token: string | null, now = Date.now()): boolean {
  const exp = expiresAt(token);
  // No readable expiry is not a reason to renew: an opaque token may be
  // perfectly valid, and renewing on every request would be worse than the
  // 401 this avoids.
  if (exp === null) return false;
  return exp - now <= RENEW_BEFORE_MS;
}

/**
 * Renew an expired dev token, once, in flight.
 *
 * A dev token lives twelve hours, so leaving the app open overnight guarantees
 * a morning of `dev token rejected: "exp" claim timestamp check failed` on
 * every page. That is a true message and a useless one: the reader has done
 * nothing wrong and there is nothing in it to act on. So a 401 renews the
 * token and retries the call, and the user sees the page they asked for.
 *
 * The promise is shared: twelve queries failing together must produce one
 * renewal, not twelve.
 */
let renewal: Promise<boolean> | null = null;

/**
 * THE STALL THIS FIXES.
 *
 * `request()` awaits a renewal BEFORE every call, which is right when a
 * renewal can succeed. When it cannot — the refresh token is gone, the
 * authority is unreachable, third-party cookies are blocked so MSAL falls
 * back to a hidden iframe — `acquireTokenSilent` does not fail quickly. It
 * waits out its own frame timeout, six seconds by default, and returns null.
 *
 * The token is left in place, so `needsRenewal` is still true, so the NEXT
 * request pays another six seconds. The shared promise above collapses
 * CONCURRENT callers but not successive ones, and with a dozen queries on a
 * 30-second refetch the desk freezes in waves for as long as somebody leaves
 * the tab open. That is the "massive stall when the session expires".
 *
 * So a failed renewal is remembered. Inside the cool-off, `renewSession`
 * answers false immediately and the request goes out and gets its fast 401 —
 * which is the honest answer and reaches the sign-in screen in milliseconds
 * instead of minutes.
 */
const RENEW_BACKOFF_MS = 30_000;
let renewFailedAt = 0;

/**
 * How long a renewal may take before we stop waiting for it.
 *
 * Bounded here as well as in MSAL's own config because this path also covers
 * the dev provider and anything else registered as a renewer. Four seconds
 * is longer than a working refresh ever takes and short enough that a person
 * does not think the page has died.
 */
const RENEW_TIMEOUT_MS = 4_000;

/** Test seam: forget that a renewal failed. */
export function resetRenewBackoff(): void {
  renewFailedAt = 0;
}

/** Is a renewal worth attempting, or did one just fail? */
export function renewalIsWorthTrying(now = Date.now()): boolean {
  return renewFailedAt === 0 || now - renewFailedAt >= RENEW_BACKOFF_MS;
}

async function renewSession(): Promise<boolean> {
  // Fail fast rather than queueing behind another doomed attempt.
  if (!renewalIsWorthTrying()) return false;

  renewal ??= (async () => {
    // The provider's own renewal, when there is one. Under Entra this
    // redeems the cached refresh token; it either sets a new token or
    // navigates to the sign-in page and never returns.
    if (renewer) {
      try {
        return (await renewer()) !== null;
      } catch {
        return false;
      }
    }

    // Seeded identities only. Under Entra this route does not exist, and
    // calling it turns "could not renew" into "could not renew, and here is
    // a 404 to investigate".
    if (authProvider !== 'dev') return false;

    const previous = oidFromToken(session.token);
    try {
      const res = await fetch('/api/v1/auth/dev-users', {
        headers: { Accept: 'application/json' },
      });
      if (!res.ok) return false;
      const { users } = (await res.json()) as { users: DevUser[] };
      const pick = pickDevIdentity(users, previous);
      if (!pick?.token) return false;
      session.token = pick.token;
      return true;
    } catch {
      return false;
    }
  })();

  try {
    /**
     * Bounded. A renewer that never settles would otherwise hold every
     * request on the page indefinitely — the failure mode is not "renewal
     * took a while", it is "the application stopped responding".
     */
    const ok = await Promise.race([
      renewal,
      new Promise<boolean>((resolve) => setTimeout(() => resolve(false), RENEW_TIMEOUT_MS)),
    ]);
    renewFailedAt = ok ? 0 : Date.now();
    return ok;
  } finally {
    renewal = null;
  }
}

async function request<T>(path: string, init: RequestInit = {}, retried = false): Promise<T> {
  // BEFORE, not after. A token about to expire is renewed while the page is
  // still working; waiting for the 401 turns every hour boundary into a
  // failed request that has to be noticed, recovered from and retried.
  if (!retried && needsRenewal(session.token)) {
    const renewed = await renewSession();
    /**
     * ALREADY EXPIRED AND UNRENEWABLE IS OVER, and saying so here saves
     * every query on the page from discovering it one failed request at a
     * time. A token still inside its life gets the benefit of the doubt —
     * the call may well succeed — but one whose `exp` has passed will 401,
     * and pretending otherwise is what produced a screen full of identical
     * errors before anything offered a way back in.
     */
    if (!renewed && hasExpired(session.token)) {
      session.token = null;
      session.signedOut = 'expired';
      onSessionLost?.();
      throw new ApiError(401, 'Your session has ended.');
    }
  }

  const headers = new Headers(init.headers);
  headers.set('Accept', 'application/json');
  // FormData sets its own Content-Type, boundary and all; overriding it
  // produces a body the server cannot parse.
  if (init.body && !(init.body instanceof FormData)) {
    headers.set('Content-Type', 'application/json');
  }
  const token = session.token;
  if (token) headers.set('Authorization', `Bearer ${token}`);

  let res: Response;
  try {
    res = await fetch(path, { ...init, headers });
  } catch (err) {
    // A network failure (server down, DNS, offline) never reaches the
    // status-based classification below, so it has to be recorded here or
    // it is invisible to telemetry entirely rather than merely misfiled.
    // Rethrown unchanged — this only observes the failure, it does not
    // change what the caller sees.
    trackError(err, 'api.network', { path: templatePath(path) });
    throw err;
  }

  if (res.status === 204) return undefined as T;

  const text = await res.text();
  const body: unknown = text ? JSON.parse(text) : null;

  if (!res.ok) {
    const b = (body ?? {}) as { message?: string; error?: string; fields?: FieldIssue[] };

    // An expired or rejected token is recoverable: renew and try once more.
    //
    // EVERY 401 ENDS HERE, INCLUDING THE RETRY'S. Handling only the first
    // attempt meant a renewed-but-still-rejected token fell through to the
    // generic error below, so the session was never declared lost and every
    // refetch tried the whole dance again — a hundred 401s in the console
    // and a server error string on screen, with nothing to stop it.
    if (res.status === 401) {
      if (!retried && (await renewSession())) return request<T>(path, init, true);

      // Not recoverable. Replace the page rather than decorating the one
      // that is open: every query on screen is about to fail the same way,
      // and a dozen identical red panels is not information.
      session.token = null;
      session.signedOut = 'expired';
      onSessionLost?.();

      throw new ApiError(401, 'Your session has ended.');
    }

    // ONLY 5xx. A 422 is the server correctly refusing bad input — the fields
    // above already carry why — and counting every one of those as a defect
    // would bury the 5xx that actually are one under routine validation
    // refusals. Recorded, not thrown differently: this is an addition, and
    // the error the caller sees is exactly what it was before this existed.
    if (res.status >= 500) {
      trackError(new Error(describeFailure(b, res.status)), 'api.status', {
        path: templatePath(path),
        status: res.status,
      });
    }

    throw new ApiError(res.status, describeFailure(b, res.status), b.fields);
  }
  return body as T;
}

/**
 * Fetch a file and hand it to the browser as a download.
 *
 * A plain `<a href>` cannot be used: every API call carries a bearer token in
 * a header, and a link carries none, so the export endpoint would answer 401.
 * So the bytes come back through fetch and go to the user as a blob URL, which
 * is revoked immediately afterwards — an un-revoked one pins the whole file in
 * memory for the life of the tab.
 */
async function download(
  path: string,
  opts: { accept?: string; fallbackName?: string } = {},
): Promise<void> {
  const headers = new Headers({ Accept: opts.accept ?? 'text/csv' });
  const token = session.token;
  if (token) headers.set('Authorization', `Bearer ${token}`);

  const res = await fetch(path, { headers });
  if (!res.ok) {
    const text = await res.text();
    let message = `that file could not be downloaded (${res.status})`;
    try {
      const body = JSON.parse(text) as { message?: string };
      if (body.message) message = body.message;
    } catch {
      /* a non-JSON error body is not worth reporting verbatim */
    }
    throw new ApiError(res.status, message);
  }

  // The server names the file; content-disposition is where that name lives.
  const disposition = res.headers.get('content-disposition') ?? '';
  const named = /filename="([^"]+)"/.exec(disposition)?.[1];

  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  try {
    const a = document.createElement('a');
    a.href = url;
    a.download = named ?? opts.fallbackName ?? 'export.csv';
    document.body.append(a);
    a.click();
    a.remove();
  } finally {
    URL.revokeObjectURL(url);
  }
}

export const api = {
  get: <T>(path: string) => request<T>(path),
  download,
  post: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: 'POST', body: body === undefined ? undefined : JSON.stringify(body) }),
  patch: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: 'PATCH', body: body === undefined ? undefined : JSON.stringify(body) }),
  put: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: 'PUT', body: JSON.stringify(body ?? {}) }),
  del: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: 'DELETE', ...(body === undefined ? {} : { body: JSON.stringify(body) }) }),
  /**
   * A multipart upload.
   *
   * NO Content-Type SET, deliberately. `FormData` carries a boundary the
   * browser generates, and naming the type by hand omits it — the server
   * then reads a body it cannot split into parts and answers 422 for a
   * request that was perfectly formed. `request()` only sets the header when
   * the body is not FormData, for the same reason.
   */
  upload: <T>(path: string, form: FormData) =>
    request<T>(path, { method: 'POST', body: form }),
  /**
   * Fetch bytes with the session token.
   *
   * THE REASON THIS HAS TO EXIST. This application authenticates with a
   * BEARER TOKEN, not a cookie, so a plain <a href> or <img src> pointing
   * at an API path sends no credential at all and gets a 401. The browser
   * then reports a failed download, or an image that will not load, with
   * nothing anywhere saying why.
   *
   * Anything that needs bytes IN the page goes through here and renders
   * from an object URL, which the caller revokes.
   */
  blob: async (path: string): Promise<Blob> => {
    const headers = new Headers();
    const token = session.token;
    if (token) headers.set('Authorization', `Bearer ${token}`);
    const res = await fetch(path, { headers });
    if (!res.ok) throw new ApiError(res.status, `could not fetch (${res.status})`);
    return res.blob();
  },
};

// ---------------------------------------------------------------------------
// Types the UI consumes. Kept narrow on purpose — the API is the contract.
// ---------------------------------------------------------------------------

export type TicketState =
  | 'new'
  | 'open'
  | 'pending_requester'
  | 'pending_external'
  | 'pending_internal'
  | 'pending_event'
  | 'resolved'
  | 'closed'
  | 'cancelled';

export interface Me {
  oid: string;
  email: string;
  /** From the directory. Null for an identity that never carried one. */
  name: string | null;
  tenants: {
    id: string;
    name: string;
    role: string | null;
    /**
     * Additive grants held BESIDE the rank role (FR-AD-06).
     *
     * The server has always sent these; this type did not declare them,
     * which is why `canReviewKb` sat unused and the KB author button was
     * gated on rank instead. A rank alone cannot answer "may this person
     * approve an article" or "may they authorize an action" — both are
     * sideways grants.
     */
    grants?: string[];
    /**
     * What this person may do in this group, exactly as the API decides it
     * — the desk's own role choices included. Screens offer a control only
     * when this holds its capability.
     */
    capabilities?: string[];
  }[];
  crossTenant: boolean;
  isIntegration: boolean;
}

export interface DevUser {
  oid: string;
  email: string;
  name: string | null;
  roles: string[];
  token: string;
}

export interface TicketListRow {
  id: string;
  number: number;
  state: TicketState;
  subject: string;
  request_type: string | null;
  systems: string[];
  project_numbers: string[];
  tags: string[];
  created_at: string;
  updated_at: string;
  /**
   * Changed since this reader last opened it.
   *
   * False for a ticket they have never opened: the absence of a read is not
   * the same as an unread, and marking every row somebody has never touched
   * would light the whole queue on their first morning.
   */
  unread: boolean;
  resolution_due_at: string | null;
  resolution_breached: boolean;
  first_response_breached: boolean;
  needs_human_routing: boolean;
  route_confidence: string | null;
  running_minutes: number;
  paused_minutes: number;
  intake_source: string;
  queue_key: string | null;
  queue_name: string | null;
  requester_name: string | null;
  /** Decided at directory sync from the person's job title or an AAD group. */
  priority: TicketPriority;
  requester_vip: boolean;
  requester_title: string | null;
  requester_email: string | null;
  assignee_oid: string | null;
  assignee_name: string | null;
  assignee_email: string | null;
  /** Their Outlook automatic replies say they are out (085). */
  assignee_away: boolean;
  /** When a scheduled absence ends; null when away with no end date. */
  assignee_away_until: string | null;
  subject_email: string | null;
  reply_count: string;
  /** Set while parked: when it is due back. */
  event_due_at: string | null;
  /** Set when it came back on its own, cleared once somebody acts on it. */
  event_fired_at: string | null;
  /** The event's name, or null if it was never parked. */
  event_name: string | null;
  /** The request this is one piece of, if any. */
  parent_number: number | null;
  /** The tickets this request became, by number. Null when it has none. */
  child_numbers: string[] | null;
  /** Tickets linked to this one as related, either way round. Null when none. */
  related_numbers: string[] | null;
}

export interface TenantConfig {
  id: string;
  name: string;
  timezone: string;
  /**
   * Features this group has, already resolved for the current person:
   * pilot membership and unmet prerequisites are applied server-side, so
   * this is what may be offered and nothing needs re-deriving from it.
   */
  modules: ModuleKey[];
  settings: {
    autoCloseDays: number;
    reopenWindowDays: number;
    autoRouteThreshold: number;
    showSlaToRequester: boolean;
    allowOnBehalfOf: boolean;
    /** Takes requests from AI agents through the intake front door. Off unless turned on. */
    assistantIntake?: boolean;
    /** This group's own mailbox. Absent means the deployment default. */
    deskEmail?: string;
    /** The request type campaign detection groups; empty is off. */
    securityReportType?: string;
  };
  /** `restricted`: only its members see its tickets (072). */
  queues: {
    id: string;
    key: string;
    name: string;
    description?: string | null;
    isDefault: boolean;
    restricted?: boolean;
    restrictedScope?: string | null;
    /** Hands new work out automatically (073). */
    autoAssign?: 'off' | 'in_turn' | 'least_open';
    /** Who it hands work to; null = every agent and queue lead. */
    autoAssignPool?: string[] | null;
  }[];
  /** What this desk can park a ticket on, newest config wins. */
  eventTypes: {
    id: string;
    key: string;
    name: string;
    description: string | null;
    defaultDays: number | null;
  }[];
  requestTypes: {
    key: string;
    label: string;
    sortOrder: number;
    measuredPurity: number | null;
    /** What belongs under it; the model reads this to choose the type (075). */
    description?: string | null;
    /** Its own acknowledgement, replacing the desk's (078). */
    acknowledgement?: string | null;
  }[];
  fields: FieldDef[];
  slaPolicies: {
    key: string;
    name: string;
    requestTypeKey: string | null;
    calendarKey: string;
    firstResponseMins: number;
    resolutionMins: number;
    atRiskPct: number;
  }[];
  calendars: {
    key: string;
    timezone: string;
    hours: { dow: number; start: string; end: string }[];
    holidays: string[];
  }[];
  agents: { oid: string; email: string; name: string | null }[];
}

export interface MatchedTicket {
  id: string;
  number: number;
  subject: string;
  state: string;
  created_at: string;
  score: number;
}

export interface ArticleMatches {
  tickets: MatchedTicket[];
  total: number;
  /** The distinguishing words the search actually ran on. */
  terms: string[];
}

export interface TextReview {
  original: { title: string; body: string };
  proposed: { title: string; body: string };
  changes: string[];
  missing: string[];
  /** Real request subjects this article answers — the requester's wording. */
  phrasings: string[];
}

export interface FieldDef {
  key: string;
  label: string;
  fieldType: string;
  validation: { options?: string[]; min?: number; max?: number; maxLength?: number };
  requiredAt: 'never' | 'create' | 'close';
  visibility: 'requester' | 'agent';
  requestTypeKeys: string[] | null;
  showWhen: { field: string; equals: unknown } | null;
}

export interface ConversationEntry {
  id: string;
  seq: number;
  kind: 'inbound' | 'reply' | 'note' | 'system';
  source: string;
  author_oid: string | null;
  author_label: string | null;
  author_name: string | null;
  author_email: string | null;
  body: string;
  created_at: string;
}

/**
 * A known answer for this ticket (FR-KB-45..49).
 *
 * `retrieval` matters to the reader: `vector` is the real thing, `lexical`
 * means the query plane was unreachable and only this system's own articles
 * were searched, by wording. The panel says which, because an agent who
 * trusts a degraded answer is worse off than one who knows it is degraded.
 */
/**
 * Work somebody handed you while you were elsewhere.
 *
 * Derived server-side from the audit trail and your own ticket read marks,
 * so there is no read state to send back: opening the ticket is what
 * removes it. See the API's services/assignments.ts.
 */
export interface AssignmentNotice {
  ticketId: string;
  number: number;
  subject: string;
  /** Null when no person did it — a routing rule, a sweep. */
  actorOid: string | null;
  actorLabel: string | null;
  source: string;
  at: string;
  automated: boolean;
}

export interface TicketSuggestion {
  /**
   * `weak` means the knowledgebase has nothing that really covers this and
   * the retriever returned its nearest neighbour — frequently a different
   * task sharing vocabulary. It must never be presented as the answer.
   */
  confidence: 'strong' | 'weak';
  articleId: string;
  title: string;
  snippet: string;
  /** The whole article, when it could be read back; else only the snippet is known. */
  body?: string;
  score: number;
  lane: 'external' | 'internal' | 'unknown';
  origin: 'tributary' | 'knowledgebase';
  collection: string;
  source: string;
  sourceUrl: string;
  updatedAt: string | null;
  /** Published where the requester can read it, and they filed anyway. */
  alreadyOffered: boolean;
  pattern: { id: string; problem: string; tickets: number } | null;
  fixes: { fix: string; cause: string | null; selfSolvable: boolean; at: string }[];
}

export interface TicketSuggestions {
  suggestions: TicketSuggestion[];
  retrieval: 'vector' | 'lexical' | 'none';
  note: string | null;
  query: string;
}

/**
 * Per-article efficacy (FR-KB-50..53). Mirror of `ArticleEfficacy` in the
 * API's kbUsage service, kept narrow.
 *
 * `deflection_rate` is computed over DECIDED auto-response outcomes only —
 * `pending` servings (still inside the reopen window) are excluded from the
 * denominator, not counted as a success. `null` means no auto-response on
 * this article has a decided outcome yet, which is a different fact from a
 * 0% rate and must never be rendered as one.
 */
/**
 * Whether the delivery queue is moving.
 *
 * `stalled` is a statement about the QUEUE, not about a process — this
 * cannot see the worker, only its effects, and the wording must not claim
 * more than that.
 */
export interface QueueHealth {
  pending: number;
  dead: number;
  oldestPendingSeconds: number | null;
  lastDeliveredAt: string | null;
  stalled: boolean;
}

export interface ArticleEfficacy {
  article_id: string;
  article_title: string | null;
  /**
   * Grouped by article AND mode, so one row is one population.
   *
   * `deflected` only applies to an auto-response and `used`/`ignored` only
   * to an agent suggestion; a merged row put numbers from two different
   * populations on the same line, with a rate whose denominator was a subset
   * of its own `served`. An article used both ways gets two rows.
   */
  mode: 'auto_response' | 'suggestion';
  served: number;
  deflected: number;
  reopened: number;
  escalated: number;
  used: number;
  ignored: number;
  pending: number;
  deflection_rate: number | null;
}

/**
 * One knowledge article as the review screen reads it (FR-KB-20..58).
 *
 * Mirror of `ArticleDetail` in the API's kb service, kept narrow. `status`
 * alone does not say whether requesters are being answered: editing a
 * published article drafts version N+1 while version N keeps serving, so a
 * screen that reads only `status` reports "draft" for an article that is live.
 */
export interface KbArticleDetail {
  id: string;
  audience: 'external' | 'internal';
  title: string;
  /** draft | in_review | approved | publishing | published | rejected | withdrawing | withdrawn */
  status: string;
  current_version: number;
  /**
   * The version the INDEX is serving, or null when it holds nothing for this
   * article. Not interchangeable with `current_version` (FR-KB-57).
   */
  published_version: number | null;
  body: string;
  drafted_by: string;
  author_name: string | null;
  author_email: string | null;
  /** 'brief' = no evidenced fix yet, so not publishable. */
  kind: 'problem' | 'brief';
  published_collection: string | null;
  /** Set only once the index has confirmed removal, not when it was asked. */
  withdrawn_at: string | null;
  /** Why it stopped being the answer — the only record of that decision. */
  withdrawn_reason: string | null;
  /**
   * Whether the index actually confirmed removal (FR-KB-58b) — NOT the same
   * question as "was a withdrawal requested". `true` = the index positively
   * answered not-found. `false` = the sweep was accepted but absence could
   * not be confirmed (usually this deployment holds only the push
   * credential); `withdrawal_note` says why, and that must never be shown as
   * confirmed removal. `null` = nothing attempted yet.
   */
  withdrawal_verified: boolean | null;
  /**
   * Explains `withdrawal_verified === false`. Also doubles as the reason a
   * withdrawal FAILED and the article was put back to `published` — a
   * `published` article carrying this note means somebody tried to withdraw
   * it and it did not work, which is otherwise invisible on this screen.
   */
  withdrawal_note: string | null;
  /** Whether THIS version carries an approval; an edit clears it (FR-KB-58). */
  current_version_approved: boolean;
  /**
   * Why a publish or withdrawal is not finishing (080): what the worker wrote
   * on the article, and the queued job it is working on.
   */
  sync?: {
    error: string | null;
    errorAt: string | null;
    attempts: number;
    job: {
      op: 'kb.publish' | 'kb.withdraw';
      attempts: number;
      lastError: string | null;
      nextAttemptAt: string | null;
      createdAt: string;
      pending: boolean;
      dead: boolean;
    } | null;
  };
  /** Published to a collection this desk no longer publishes to or searches. */
  stranded?: { publishedCollection: string; currentCollection: string } | null;
  reviews: { decision: string; reason: string | null; at: string; reviewer: string | null }[];
  evidence: { id: string; number: number; subject: string }[];
}

/**
 * The most recent pattern-mining sweep (FR-KB-59). Mirror of `MiningRun` in
 * the API's mining service.
 *
 * `null` (not this type) means a sweep has never run at all — a different
 * statement from an `ok` run that found nothing, and the two must not be
 * captioned the same way.
 */
export interface MiningRun {
  id: string;
  started_at: string;
  finished_at: string | null;
  outcome: 'running' | 'ok' | 'failed';
  error: string | null;
  patterns_found: number;
  patterns_new: number;
  trigger: 'scheduled' | 'manual';
}

export interface TicketLinkRow {
  id: string;
  type: string;
  /** True when this ticket is the row's `from` end. */
  outgoing: boolean;
  /** Already worded for the end that asked — "Parent of" or "Child of". */
  label: string;
  /** False for links the system wrote; those cannot be removed by hand. */
  manual: boolean;
  otherNumber: number;
  otherTenantId: string;
  otherSubject: string;
  otherState: TicketState;
  otherAssigneeName: string | null;
  createdAt: string;
  actorName: string | null;
}

export interface TicketDetail {
  /** Files on this ticket. A note’s files are absent for a requester. */
  attachments?: {
    id: string;
    conversationId: string | null;
    filename: string;
    contentType: string;
    sizeBytes: number;
    scan: 'scan_pending' | 'scan_clean' | 'scan_infected' | 'scan_skipped';
    createdAt?: string;
  }[];

  /** Everything this ticket is bound to, in both directions. */
  links: TicketLinkRow[];
  /** The event this ticket is parked on, named. Null when it is not parked. */
  eventName?: string | null;
  ticket: TicketListRow & {
    tenant_id: string;
    custom: Record<string, unknown>;
    requester_oid: string | null;
    subject_oid: string | null;
    assignee_oid: string | null;
    first_responded_at: string | null;
    matched_template: string | null;
    routed_by: string | null;
    resolved_at: string | null;
    closed_at: string | null;
  };
  queue: { id: string; key: string; name: string } | null;
  requester: {
    oid?: string;
    email: string;
    name: string | null;
    office?: string | null;
    vip?: boolean;
    job_title?: string | null;
  } | null;
  subjectPerson: { oid: string; email: string; name: string | null } | null;
  assignee: {
    oid: string;
    email: string;
    name: string | null;
    /** Their Outlook automatic replies say they are out (085). */
    away?: boolean;
    awayUntil?: string | null;
  } | null;
  conversation: ConversationEntry[];
  audit: {
    field: string;
    from_value: string | null;
    to_value: string | null;
    note: string | null;
    at: string;
    actor_label: string | null;
    source: string;
  }[];
  periods: {
    state: TicketState;
    entered_at: string;
    exited_at: string | null;
    reason: string | null;
    blocked_party: string | null;
    business_minutes: number | null;
  }[];
  slaPolicy: { key: string; firstResponseMins: number; resolutionMins: number; atRiskPct: number };
  classification: {
    /** The email ladder's; null for any other source (083). */
    rung: number | null;
    rung_label: string | null;
    /** Where the ticket came from: email, portal, teams, assistant, api. */
    source: string;
    proposed_request_type: string | null;
    proposed_queue_key: string | null;
    confidence: string | null;
    matched_rule: string | null;
    /** What decided the type (081): chosen | subject (the model) | ladder. Null before 081. */
    type_by: string | null;
    human_request_type: string | null;
    overridden_at: string | null;
  }[];
  legalTransitions: TicketState[];
  showSla: boolean;
}

export interface DurationSummary {
  n: number;
  median: number | null;
  p90: number | null;
  tailRatio: number | null;
  min: number | null;
  max: number | null;
}

export interface Overview {
  tenantId: string;
  /** Confidential tickets (restricted queues) left out of these figures. */
  hiddenConfidential?: number;
  volume: number;
  byState: Record<string, number>;
  bySource: Record<string, number>;
  firstResponse: DurationSummary;
  resolution: DurationSummary;
  slaAttainment: { measured: number; met: number; rate: number | null };
  dailyVolume: { day: string; created: number; resolved: number }[];
  backlog: { open: number; olderThan7Days: number; unassigned: number; needsHumanRouting: number };
  topRequestTypes: {
    requestType: string;
    tickets: number;
    medianHours: number | null;
    p90Hours: number | null;
    breachRate: number | null;
  }[];
}

export interface ComparisonRow {
  tenantId: string;
  tenantName: string;
  volume: number;
  medianResolutionHours: number | null;
  p90ResolutionHours: number | null;
  medianFirstResponseHours: number | null;
  slaBreachRate: number | null;
  openBacklog: number;
  medianRepliesPerTicket: number | null;
  blockedShare: number | null;
}

export interface BlockedTimeReport {
  byState: { state: string; periods: number; totalHours: number; medianHours: number | null; p90Hours: number | null }[];
  byParty: { party: string; periods: number; totalHours: number; medianHours: number | null }[];
  byRequestType: { requestType: string; blockedShare: number; totalBlockedHours: number }[];
  topReasons: { reason: string; periods: number }[];
}

export interface SecurityCampaign {
  id: string;
  basis: 'content' | 'rate';
  status: 'open' | 'confirmed' | 'dismissed';
  matched_on: string[];
  senders: string[];
  link_domains: string[];
  /** Distinct people, not tickets — the whole point of the grouping. */
  reporters: string[];
  first_seen_at: string;
  last_seen_at: string;
  detected_at: string;
  note: string | null;
  ticket_count: number;
  tickets: { id: string; number: number; subject: string }[];
}

export interface FieldHealthRow {
  key: string;
  label: string;
  consumer: string;
  /** 'live' = a field this system defines; 'legacy' = one FreshService sent. */
  origin: 'live' | 'legacy';
  tickets: number;
  filled: number;
  fillRate: number;
  distinctValues: number;
  dominantValue: string | null;
  dominantShare: number | null;
  suspect: boolean;
  reason: string | null;
}

export interface AutomationReport {
  totalClosed: number;
  closedWithNoHumanReply: number;
  zeroTouchRate: number | null;
  byRequestType: { requestType: string; closed: number; zeroTouch: number; share: number }[];
  suppressedMail: { rule: string; messages: number }[];
  templateMatches: { template: string; tickets: number; medianMinutes: number | null }[];
  classificationByRung: { rung: number; tickets: number }[];
  humanOverrides: {
    total: number;
    rate: number | null;
    bySource: { source: string; tickets: number; overridden: number; rate: number | null }[];
  };
  /** Per queue routing proposed: how often a person moved it, and where to (084). */
  queueMoves: { queue: string; tickets: number; moved: number; rate: number | null; to: { queue: string; tickets: number }[] }[];
}

/** GET /reports/suggested-replies (082): what became of suggested replies, per agent. */
export interface SuggestedRepliesReport {
  window: { from: string; to: string };
  byAgent: {
    agent: string;
    suggested: number;
    dismissed: number;
    usedNotSent: number;
    sent: number;
    sentUnchanged: number;
    meanSimilarity: number | null;
    ignored: number;
  }[];
}

// ---------------------------------------------------------------------------
// Detailed reports. Narrow mirrors of the server shapes — the API is the
// contract, and a client type that guesses at extra fields is a lie the
// compiler will happily enforce.
// ---------------------------------------------------------------------------

export interface AgentWorkloadRow {
  oid: string | null;
  name: string | null;
  email: string | null;
  assigned: number;
  resolved: number;
  medianHours: number | null;
  p90Hours: number | null;
  breachRate: number | null;
  openNow: number;
  blockedNow: number;
  oldestOpenDays: number | null;
  untouchedShare: number | null;
}

export interface AgentWorkloadReport {
  rows: AgentWorkloadRow[];
  unassigned: { openNow: number; oldestOpenDays: number | null };
  loadGini: number | null;
}

export interface BacklogAgeingReport {
  buckets: { label: string; tickets: number; breached: number; unassigned: number }[];
  byQueue: { queue: string; counts: number[]; total: number }[];
  neverAnswered: { tickets: number; medianAgeDays: number | null };
  oldest: {
    number: number;
    subject: string;
    requestType: string | null;
    queue: string | null;
    assignee: string | null;
    state: TicketState;
    ageDays: number;
    breached: boolean;
  }[];
}

export interface DemandShapeReport {
  heatmap: number[][];
  outOfHours: { tickets: number; share: number | null };
  monthly: { month: string; created: number; resolved: number; breached: number }[];
  systems: { system: string; tickets: number; breachRate: number | null }[];
  departments: { department: string; tickets: number; requesters: number }[];
  offices: { office: string; tickets: number }[];
}

export interface PercentileRow {
  label: string;
  n: number;
  p50: number | null;
  p75: number | null;
  p90: number | null;
  p95: number | null;
  max: number | null;
}

export interface ResponseQualityReport {
  firstResponse: DurationSummary;
  resolution: DurationSummary;
  byRequestType: PercentileRow[];
  byQueue: PercentileRow[];
  monthlyAttainment: {
    month: string;
    measured: number;
    met: number;
    rate: number | null;
    firstResponseMeasured: number;
    firstResponseMet: number;
    firstResponseRate: number | null;
  }[];
  pausedShare: { resolved: number; withPause: number; medianPausedShare: number | null };
}

/**
 * Mix-adjusted desk performance (FR-RPT-09..11). Mirror of `PeriodReport`,
 * `Baseline` and `MixAdjustedReport` in the API's mixAdjusted service, kept
 * narrow.
 *
 * `crude`/`adjusted` are `number | null` independently: a period can have no
 * resolved work at all (both null), or resolved work the baseline cannot
 * speak to (`adjusted` null, `crude` not). Collapsing that distinction to a
 * single dash is exactly the misreading this report exists to prevent.
 */
export interface MixAdjustedFigure {
  crude: number | null;
  adjusted: number | null;
  n: number;
  /** Baseline strata this period has no observations for. */
  missingStrata: string[];
  /** Share of the baseline's weight the present strata account for. */
  baselineCoverage: number;
}

export interface MixShiftRow {
  key: string;
  baselineShare: number;
  currentShare: number;
  /** Percentage points, current minus baseline. */
  delta: number;
}

export interface MixAdjustedPeriod {
  period: string;
  /** All demand, including what the assistant answered. */
  demand: number;
  deflected: number;
  deflectedShare: number;
  /** Minutes. Measured over work a PERSON did — see the service comment. */
  resolution: MixAdjustedFigure;
  /** Rate 0-1. */
  attainment: MixAdjustedFigure;
  mix: MixShiftRow[];
}

export interface MixAdjustedBaseline {
  id: string;
  label: string;
  mix: Record<string, number>;
  window_from: string;
  window_to: string;
  sample_size: number;
  note: string | null;
  pinned_at: string;
  pinned_by_name: string | null;
}

export interface MixAdjustedReport {
  baseline: MixAdjustedBaseline | null;
  periods: MixAdjustedPeriod[];
  /** Non-null only when no baseline is pinned — said plainly, not as zeroes. */
  note: string | null;
}

/**
 * Daylighting (FR-RPT-12): two questions answered straight from the record,
 * no model involved. Mirror of `StalledTicket` and `AutomationCandidate` in
 * the API's daylight service, kept narrow — see that file's comment for why
 * `hoursPerYear` is null far more often than it is a number.
 */
export type StallReason =
  | 'never_answered'
  | 'awaiting_us'
  | 'awaiting_requester'
  | 'awaiting_third_party'
  | 'unassigned'
  | 'unrouted'
  | 'moving';

export interface StalledTicket {
  id: string;
  number: number;
  subject: string;
  state: TicketState;
  requestType: string | null;
  queue: string | null;
  assignee: string | null;
  /** Working minutes since the ticket arrived — the SLA clock, not wall-clock age. */
  ageBusinessMinutes: number;
  reason: StallReason;
  /** One line, stating the fact. No adjectives. */
  finding: string;
  /** The single next action. Imperative, addressed to the desk. */
  nextAction: string;
  /** How stuck, 0..1, for ORDERING only — not a percentage of anything. */
  severity: number;
}

export interface AutomationCandidate {
  requestType: string;
  /** Extrapolated tickets per year from the observed window. */
  perYear: number;
  /**
   * Working hours per year — only when real desk time is known. `null` means
   * "volume known, handling time unknown", which is the normal case: see the
   * assumptions on each case for why.
   */
  hoursPerYear: number | null;
  ticketsPerRequester: number;
  verdict: 'ready' | 'candidate' | 'needs_work' | 'not_worth_it';
  /** Why that verdict, in one line. */
  rationale: string;
  /** What would have to be true. Empty when the verdict is `ready`. */
  blockers: string[];
  /** The assumptions the numbers rest on, stated so they can be argued with. */
  assumptions: string[];
  tickets: number;
  requesters: number;
}

export interface RecurrenceReport {
  repeatByType: {
    requestType: string;
    tickets: number;
    requesters: number;
    repeatRequesters: number;
    perRequester: number;
    medianDaysBetween: number | null;
  }[];
  topRequesters: {
    email: string | null;
    name: string | null;
    department: string | null;
    tickets: number;
    distinctTypes: number;
    topType: string | null;
  }[];
  pairs: {
    email: string | null;
    requestType: string;
    occurrences: number;
    medianDaysBetween: number | null;
  }[];
}

// ---------------------------------------------------------------------------
// Telemetry (platform_admin only). Mirror of the shapes the telemetry routes
// return — see db/migrations/050_telemetry.sql for the tables behind them.
// Not tenant-scoped: this is the platform's own behaviour, not a group's.
// ---------------------------------------------------------------------------

export type TelemetryIssueState = 'open' | 'acknowledged' | 'muted' | 'resolved';

/**
 * One grouped error, as the worklist reads it (`telemetry_issues`).
 *
 * `resolvedAt` is marked optional rather than assumed present: the issues-list
 * endpoint's documented shape does not include it, only the detail endpoint is
 * certain to. When it IS there and `state` has gone back to `open`, that is
 * the "reopened" case migration 050 calls out — last_seen_at moved past
 * resolved_at, meaning the fix did not hold — and the row must say so rather
 * than reading as a fresh, never-touched issue.
 */
export interface TelemetryIssue {
  fingerprint: string;
  name: string;
  message: string;
  events: number;
  firstSeenAt: string;
  lastSeenAt: string;
  state: TelemetryIssueState;
  source: string;
  release: string | null;
  resolvedAt?: string | null;
}

/** One occurrence, as stored in `telemetry_events`. */
export interface TelemetryEvent {
  id: number;
  at: string;
  source: string;
  kind: string;
  name: string;
  durationMs: number | null;
  status: number | null;
  tenantId: string | null;
  userOid: string | null;
  errorMessage: string | null;
  errorStack: string | null;
  release: string | null;
  meta: Record<string, unknown>;
}

/** The single-issue view: the worklist row plus the sample stack and note. */
export interface TelemetryIssueDetail extends TelemetryIssue {
  stack: string | null;
  note: string | null;
}

export interface TelemetryOverview {
  requests: number;
  errors: number;
  errorRate: number;
  p50Ms: number;
  p95Ms: number;
  activeUsers: number;
  byDay: { day: string; requests: number; errors: number }[];
}

export interface TelemetryRoute {
  name: string;
  calls: number;
  p95Ms: number;
  errorRate: number;
}

export interface TelemetryUsageRow {
  name: string;
  kind: string;
  count: number;
}

export interface TelemetryWorkerJob {
  name: string;
  runs: number;
  failures: number;
  p95Ms: number;
}

/**
 * Live updates (GET /api/v1/stream): server-sent events, read with fetch so
 * the token goes in a header like every other call — the browser's
 * EventSource cannot send one.
 *
 * Resolves when the stream ends (the server closes it hourly so the session
 * is checked again, or the network drops), with `none` when the server says
 * there is nothing for this person to follow (204), so the caller can stop
 * trying and keep polling.
 */
export async function readChangeStream(
  onEvent: (event: string, data: string) => void,
  signal: AbortSignal,
): Promise<'ended' | 'none' | 'refused'> {
  if (needsRenewal(session.token)) await renewSession();
  const headers = new Headers({ Accept: 'text/event-stream' });
  if (session.token) headers.set('Authorization', `Bearer ${session.token}`);
  const res = await fetch('/api/v1/stream', { headers, signal, cache: 'no-store' });
  if (res.status === 204) return 'none';
  if (!res.ok || !res.body) return 'refused';

  const reader = res.body.pipeThrough(new TextDecoderStream()).getReader();
  let buffer = '';
  for (;;) {
    const { value, done } = await reader.read();
    if (done) return 'ended';
    buffer += value;
    // Events are separated by a blank line; a line starting ':' is a comment.
    let cut: number;
    while ((cut = buffer.indexOf('\n\n')) >= 0) {
      const block = buffer.slice(0, cut);
      buffer = buffer.slice(cut + 2);
      let event = 'message';
      const data: string[] = [];
      for (const line of block.split('\n')) {
        if (line.startsWith(':')) continue;
        if (line.startsWith('event:')) event = line.slice(6).trim();
        else if (line.startsWith('data:')) data.push(line.slice(5).trimStart());
      }
      if (data.length || event !== 'message') onEvent(event, data.join('\n'));
    }
  }
}
